import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { loginWithGoogle } from "@/services/auth";
import { useState } from "react";

interface GoogleSignInButtonProps {
  onError?: (error: string) => void;
}

export function GoogleSignInButton({ onError }: GoogleSignInButtonProps) {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleGoogleSignIn = async () => {
    // Prevent multiple clicks
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      // Open the popup window immediately on user click
      const popup = window.open(
        "about:blank",
        "oauth",
        "height=600,width=800,status=yes,toolbar=no,menubar=no,location=no",
      );

      if (!popup) {
        throw new Error(
          "Failed to open the authentication window. Please allow popups for this site.",
        );
      }

      // Set up a check to detect if the popup is closed without completing auth
      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          setIsProcessing(false);
        }
      }, 3000);

      try {
        // Then proceed with the OAuth flow using the already opened window
        const authData = await loginWithGoogle(popup);
        clearInterval(popupCheckInterval); // Clear the interval when auth completes
        if (authData) {
          navigate("/");
        }
      } catch (err) {
        clearInterval(popupCheckInterval); // Clear the interval on error
        throw err; // Re-throw to be caught by the outer catch
      }
    } catch (err: any) {
      if (onError) {
        onError(err?.message || "Failed to sign in with Google");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      variant="outline"
      type="button"
      className="w-full h-11"
      onClick={handleGoogleSignIn}
    >
      <img src="/google.svg" alt="Google" className="w-5 h-5 mr-2" />
      Continue with Google
    </Button>
  );
}
